import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://cssbuy.info';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main>
        <Navbar />
        <section className="relative w-full bg-background-50 pt-24 md:pt-32 pb-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mb-4">Artigo não encontrado</h1>
            <p className="text-foreground-500 mb-6">O artigo que você procura não está disponível.</p>
            <a href="/" className="inline-flex items-center gap-2 bg-accent-500 text-white font-semibold px-6 py-3 rounded-full text-sm hover:bg-accent-600 transition-colors cursor-pointer">
              Voltar para Início
            </a>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  const articleUrl = `${siteUrl}/artigo/${article.slug}`;
  const imageUrl = article.image;

  // Build JSON-LD
  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: imageUrl,
    datePublished: '2026-06-01',
    dateModified: '2026-06-27',
    author: {
      '@type': 'Organization',
      name: 'Equipe CSSBuy Info',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'CSSBuy Info',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Início',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Guia de Compras',
        item: `${siteUrl}/guia-de-compras`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <Navbar />

      {/* Article Hero */}
      <section className="relative w-full bg-background-50 pt-24 md:pt-32 pb-12 md:pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8" aria-label="Breadcrumb">
            <a href="/" className="hover:text-accent-500 transition-colors cursor-pointer">Início</a>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <a href="/guia-de-compras" className="hover:text-accent-500 transition-colors cursor-pointer">Guia de Compras</a>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <span className="text-foreground-700 font-medium truncate max-w-[200px]">{article.title}</span>
          </nav>

          <span className="inline-block bg-accent-100 text-accent-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-4">
            {article.category}
          </span>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-950 leading-tight mb-6">
            {article.title}
          </h1>
          <p className="text-foreground-600 text-sm md:text-base max-w-2xl leading-relaxed mb-6">
            {article.description}
          </p>

          {/* Author & Meta */}
          <div className="flex flex-wrap items-center gap-6 text-xs text-foreground-500">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-accent-200 flex items-center justify-center">
                <span className="text-accent-700 font-bold text-xs">CI</span>
              </div>
              <span className="font-medium text-foreground-700">Equipe CSSBuy Info</span>
            </div>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i> Atualizado em {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i> {article.readTime} de leitura
            </span>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="relative w-full bg-background-50 pb-8">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="relative w-full aspect-[2/1] rounded-2xl overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="relative w-full bg-background-100 py-8">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="bg-background-50 border border-background-200/60 rounded-2xl p-6">
            <h2 className="text-foreground-950 font-heading font-bold text-lg mb-4">Índice</h2>
            <nav className="flex flex-col gap-2">
              {article.sections.map((section, idx) => (
                <a
                  key={idx}
                  href={`#section-${idx + 1}`}
                  className="text-foreground-600 hover:text-accent-500 text-sm transition-colors cursor-pointer"
                >
                  {idx + 1}. {section.heading}
                </a>
              ))}
              <a
                href="#faq"
                className="text-foreground-600 hover:text-accent-500 text-sm transition-colors cursor-pointer"
              >
                {article.sections.length + 1}. Perguntas Frequentes
              </a>
            </nav>
          </div>
        </div>
      </section>

      {/* Article Body */}
      <article className="relative w-full bg-background-50 py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="space-y-12">
            {article.sections.map((section, sIdx) => (
              <div key={sIdx} id={`section-${sIdx + 1}`}>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                  {section.heading}
                </h2>
                {section.content.map((paragraph, pIdx) => (
                  <p key={pIdx} className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}
                {section.listItems && (
                  <ul className="space-y-3 mb-4">
                    {section.listItems.map((item, lIdx) => (
                      <li key={lIdx} className="flex items-start gap-3 text-sm text-foreground-600">
                        <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-accent-600 text-xs"></i>
                        </span>
                        <span>
                          <strong className="text-foreground-800">{item.title}:</strong> {item.desc}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {section.tips && (
                  <ul className="space-y-3 mb-4">
                    {section.tips.map((tip, tIdx) => (
                      <li key={tIdx} className="flex items-start gap-3 text-sm text-foreground-600">
                        <span className="w-5 h-5 flex items-center justify-center rounded-full bg-secondary-100 flex-shrink-0 mt-0.5">
                          <i className="ri-lightbulb-line text-secondary-600 text-xs"></i>
                        </span>
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {section.table && (
                  <div className="overflow-x-auto mb-6">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b border-background-200">
                          {section.table.headers.map((h, hIdx) => (
                            <th key={hIdx} className="text-left py-3 px-4 text-foreground-950 font-semibold text-xs">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {section.table.rows.map((row, rIdx) => (
                          <tr key={rIdx} className="border-b border-background-200">
                            {row.map((cell, cIdx) => (
                              <td key={cIdx} className="py-3 px-4 text-xs text-foreground-600">{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}

            {/* FAQ Section */}
            <div id="faq">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                Perguntas Frequentes
              </h2>
              <div className="space-y-4">
                {article.faqs.map((faq, fIdx) => (
                  <div key={fIdx} className="bg-background-100 border border-background-200/60 rounded-2xl p-5">
                    <h3 className="text-foreground-950 font-semibold text-sm mb-2">{faq.question}</h3>
                    <p className="text-foreground-600 text-xs leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Internal Link to Home */}
            <div className="bg-accent-50 border border-accent-200 rounded-2xl p-6 text-center">
              <p className="text-foreground-700 text-sm mb-3">
                Quer explorar todos os produtos organizados por categoria?
              </p>
              <a
                href="/"
                className="inline-flex items-center gap-2 bg-accent-500 text-white font-semibold px-6 py-3 rounded-full whitespace-nowrap hover:bg-accent-600 transition-colors duration-200 cursor-pointer text-sm"
              >
                Voltar para a Página Inicial
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </article>

      {/* CTA */}
      <section className="relative w-full flex flex-col lg:flex-row min-h-[400px]">
        <div className="relative flex-1 min-h-[280px] md:min-h-0 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Clean%20contemporary%20retail%20display%20with%20multiple%20pairs%20of%20fashionable%20sneakers%20on%20warm%20wooden%20shelving%2C%20soft%20ambient%20boutique%20lighting%2C%20curated%20product%20arrangement%2C%20beige%20and%20cream%20interior%20design%2C%20upscale%20sneaker%20store%20atmosphere%2C%20lifestyle%20retail%20photography%2C%20warm%20inviting%20commercial%20space&width=960&height=700&seq=article-cta-v2&orientation=landscape"
            alt="CSSBuy Spreadsheet - Comece Agora"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="absolute bottom-0 left-0 p-10">
            <h2 className="text-white font-heading font-bold text-3xl md:text-4xl leading-tight">
              Pronto para<br />começar?
            </h2>
          </div>
        </div>
        <div className="flex-1 bg-background-50 flex flex-col items-center justify-center py-14 px-6 md:px-14">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-4">
            Acesse o CSSBuy<br />Spreadsheet agora
          </h2>
          <p className="text-foreground-600 text-sm text-center max-w-md leading-relaxed mb-8">
            Mais de 10 mil usuários já confiam na nossa planilha. Links verificados, produtos de qualidade e atualização diária.
          </p>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="group inline-flex items-center gap-2 bg-primary-500 text-white font-semibold px-10 py-4 rounded-full whitespace-nowrap hover:bg-primary-600 transition-colors duration-200 cursor-pointer text-sm"
          >
            Comprar Agora
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
          </a>
        </div>
      </section>

      <Footer />
    </main>
  );
}